import { InputRichText, InputRichTextProps } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import EmojiOne from 'emojione';
import { ConversationsChannels } from 'lib-shared-legacy-types';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import memoize from 'memoize-one';
import * as React from 'react';
import { RefObject, useEffect } from 'react';

import {
  CreateAttachmentApiArg,
  CreateAttachmentApiResponse,
} from 'apis/files';
import { getFileSize } from 'app/utils/attachments';
import {
  channelToSanitizationProfile,
  SanitizeHTML,
} from 'app/utils/htmlSanitizer';
import markdownToHTML from 'app/utils/markdownToHTML';
import supportedLanguages, {
  DEFAULT_LANG_CODE,
} from 'app/utils/supportedLanguages';
import { WithTemplatesInjectedProps } from 'hocs';
import {
  CommandGroup,
  commandGroupDelimiter,
  useCommands,
} from 'utils/commands';

import { StateBar } from './components/stateBar/stateBar';
import { ValidationState } from './components/stateBar/stateBar.types';
import styles from './richTextField.module.scss';
import turndownService from './utils/turndown-service';

const MAX_ATTACHMENTS_TOTAL_FILE_SIZE = 20097152; // ~20mb.
const MAX_INLINE_FILE_SIZE = 100000000; // ~100mb.

export type Channel = ConversationsChannels | 'notes' | 'bulk';

export type NormalizedTemplate = {
  name: string;
  subject: string;
  content: string;
};

export type PropsT = WithTemplatesInjectedProps<NormalizedTemplate> & {
  channel: Channel;
  language?: string;
  disableSubmitMessageWithEnter?: boolean;
  submitMessageWithEnter: boolean;
  requesterName?: string;
  templateContext?: any;
  className?: string;
  stateBarClassName?: string;
  label?: string;
  name?: string;
  required?: boolean;
  placeholder?: string;
  attachments?: File[];
  attachmentsFields?: any;
  onFileUploadingChange?: (state: boolean) => void;
  /** Redux form name */
  form?: string;
  arrayPush: (type: string, payload: any, attachment: any) => void;
  personalSubmitMessageWithEnter: (payload: any) => void;
  onChange?: (value: string) => void;
  onTriggerSend?: () => void;
  uploadAttachment: (
    payload: CreateAttachmentApiArg,
  ) => Promise<CreateAttachmentApiResponse>;
  renderTemplate: (payload: { content: string }) => Promise<any>;
  setValidationState?: (validation: ValidationState) => void;
  validation?: any;
  hasFFWriteHTMLEmail: boolean;
  hasFFRecommendedQuickReplies: boolean;
  hasLanguageTranslationControlFF: boolean;
  editorWindow?: any;
  onMount?: () => void;
  onUnmount?: (html: string) => void;
  hasBorder?: boolean;
  id?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  autocomplete?: {
    hasAutocompleteSuggestionsFeature: boolean;
    showAutocompleteSuggestions: boolean;
    onToggleShowAutocompleteSuggestions: () => void;
  };
  hideSendOnEnterToggle?: boolean;
  isPublicReply?: boolean;
  toolbarAddons?: React.ReactNode;
};

type StateT = {
  fileStatuses: any;
  editorText: string;
  filesUploadingCount: number;
  keybindings?: InputRichTextProps['keybindings'];
};

export default class RichTextField extends React.Component<PropsT, StateT> {
  editorRef: RefObject<InputRichText>;

  static defaultProps: Partial<PropsT> = {
    onMount: noop,
    onUnmount: noop,
    onKeyDown: noop,
    form: 'richTextFiledNew' + Math.random(),
    language: DEFAULT_LANG_CODE,
  };

  constructor(props: PropsT) {
    super(props);

    this.state = {
      fileStatuses: {},
      editorText: '',
      filesUploadingCount: 0,
    };
    this.editorRef = React.createRef();
    this.getQuickResponsesApi = memoize(this.getQuickResponsesApi, isEqual);
    this.getLanguages = memoize(this.getLanguages);
  }

  sendOnEnter(): boolean {
    const { channel, submitMessageWithEnter, disableSubmitMessageWithEnter } =
      this.props;

    if (disableSubmitMessageWithEnter === true) {
      return false;
    }

    return (
      submitMessageWithEnter &&
      !['email', 'contactform', 'notes', 'bulk'].includes(channel)
    );
  }

  isFormattingAllowed(): boolean {
    const { channel } = this.props;

    return [
      'email',
      'contactform',
      'widgetchat',
      'notes',
      'messenger',
    ].includes(channel);
  }

  getPlaceholderText(): string {
    const { requesterName, placeholder } = this.props;
    if (placeholder) {
      return placeholder;
    }
    const suffix = this.sendOnEnter() ? ' (send with <enter>)' : '';
    if (requesterName) {
      return `Message ${requesterName}${suffix}`;
    }
    return `Type your message${suffix}`;
  }

  updateFileUploadingStatus(status: 'start' | 'end') {
    let filesUploadingCount = this.state.filesUploadingCount;
    const prevIsUploading = filesUploadingCount > 0;
    if (status === 'start') {
      filesUploadingCount++;
    }
    if (status === 'end') {
      filesUploadingCount--;
    }
    this.setState({ filesUploadingCount });
    const isUploading = filesUploadingCount > 0;
    if (this.props.onFileUploadingChange && prevIsUploading !== isUploading) {
      this.props.onFileUploadingChange(isUploading);
    }
  }

  getMarkdown = (): string => {
    const { current } = this.editorRef;
    if (current) {
      return turndownService.turndown(current.getSanitizedHtml());
    }
    return '';
  };

  getText = (): string => {
    const { current } = this.editorRef;
    if (current) {
      return current.getSanitizedHtml();
    }
    return '';
  };

  getPlainText = (): string => {
    const { current } = this.editorRef;
    if (current) {
      return current.getPlainText();
    }
    return '';
  };

  setText = (text: string): void => {
    const { current } = this.editorRef;
    if (current && typeof text === 'string') {
      current.setHtml(text);
    }
  };

  setMarkdown(markdown: string): void {
    const markup = markdownToHTML(EmojiOne.shortnameToUnicode(markdown));
    const fixParagraphs = markup
      .replace(/<p>/g, '<div>')
      .replace(/<\/p>/g, '</div><br />');
    this.setText(fixParagraphs);
  }

  focus = (): void => {
    const { current } = this.editorRef;
    if (current) {
      current.focus();
    }
  };

  handleToggleSendOnEnter = (): void => {
    const { submitMessageWithEnter, personalSubmitMessageWithEnter } =
      this.props;
    personalSubmitMessageWithEnter({
      key: 'submitMessageWithEnter',
      value: !submitMessageWithEnter,
    });
  };

  handleTriggerSend = (): void => {
    const { onTriggerSend } = this.props;
    if (typeof onTriggerSend === 'function') {
      onTriggerSend();
    }
  };

  handleChange = (): void => {
    const editorText = this.editorRef.current?.getPlainText() ?? '';
    this.setState({ editorText });
    const { onChange } = this.props;

    if (typeof onChange === 'function') {
      onChange(editorText);
    }
  };

  handleInlineUpload = (file: File, uid: string): void => {
    const { uploadAttachment, setValidationState } = this.props;

    this.setState(({ fileStatuses }) => ({
      fileStatuses: { ...fileStatuses, [uid]: { status: 'loading' } },
    }));
    if (file.size > MAX_INLINE_FILE_SIZE) {
      if (setValidationState) {
        setValidationState({
          touched: true,
          valid: true,
          warning: {
            type: 'file-size',
            arg: {
              size: getFileSize(file.size),
              maxSize: getFileSize(MAX_INLINE_FILE_SIZE),
            },
          },
        });
        this.setState(({ fileStatuses }) => ({
          fileStatuses: { ...fileStatuses, [uid]: { status: 'failed' } },
        }));
      }
    } else {
      this.updateFileUploadingStatus('start');

      uploadAttachment({ access: 'public', file: file })
        .then((response) => {
          this.setState(({ fileStatuses }) => ({
            fileStatuses: {
              ...fileStatuses,
              [uid]: {
                status: 'succeeded',
                link: response.data.absolute_uri,
              },
            },
          }));
        })
        .catch((exception: unknown) => {
          this.setState(({ fileStatuses }) => ({
            fileStatuses: { ...fileStatuses, [uid]: { status: 'failed' } },
          }));
          console.warn('handleInlineUpload exception', exception);
        })
        .finally(() => {
          this.updateFileUploadingStatus('end');
        });
    }
  };

  handleAttachmentUpload = (file: File, uid: string): void => {
    const { attachments, form, setValidationState, channel, uploadAttachment } =
      this.props;

    const totalSum =
      Array.isArray(attachments) && attachments.length > 0
        ? attachments
            .map((a) => a.size)
            .filter((size: number) => !isNaN(size))
            .reduce((sum: number, current: number) => sum + current, 0)
        : 0;

    if (MAX_ATTACHMENTS_TOTAL_FILE_SIZE > totalSum + file.size) {
      this.updateFileUploadingStatus('start');

      uploadAttachment({
        file,
        access: [
          'adachat',
          'digitalgeniuschat',
          'kindlychat',
          'widgetchat',
          'messenger',
        ].includes(channel)
          ? 'public'
          : 'private',
      })
        .then((response) => {
          const attachment = {
            name: file.name,
            absolute_uri: response.data.absolute_uri,
            relative_uri: response.data.relative_uri,
            size: file.size,
            uid: uid,
          };
          this.props.arrayPush(form as string, 'attachments', attachment);
        })
        .catch((exception: unknown) => {
          this.setState(({ fileStatuses }) => ({
            fileStatuses: { ...fileStatuses, [uid]: { status: 'failed' } },
          }));
          console.warn('handleAttachmentUpload exception', exception);
        })
        .finally(() => {
          this.updateFileUploadingStatus('end');
        });
    } else {
      console.warn(
        `Total attachment size is too large: (size: ${getFileSize(
          totalSum + file.size,
        )}, max ${getFileSize(MAX_ATTACHMENTS_TOTAL_FILE_SIZE)}.`,
      );

      setValidationState?.({
        touched: true,
        valid: true,
        warning: {
          type: 'file-size',
          arg: {
            size: getFileSize(totalSum + file.size),
            maxSize: getFileSize(MAX_ATTACHMENTS_TOTAL_FILE_SIZE),
          },
        },
      });
    }
  };

  @boundMethod
  handleError(error: Error): void {
    if (error.name === 'IndexSizeError') {
      Sentry.captureException(error, {
        tags: { team: SentryTeam.Core },
      });
      return;
    }
    throw error;
  }

  renderTemplate = (item: { content: string }): Promise<string> => {
    const { renderTemplate } = this.props;
    return renderTemplate(item).then((result: any) => {
      ampli.editorQuickResponseAdded();
      return result.data.rendered_content;
    });
  };

  getLanguages(defaultLanguage) {
    return supportedLanguages.concat().sort((a, b) => {
      if (a.code === defaultLanguage) {
        return -1;
      }
      if (b.code === defaultLanguage) {
        return 1;
      }
      return 0;
    });
  }

  getQuickResponsesApi(
    templates: WithTemplatesInjectedProps<NormalizedTemplate>['templates'],
  ) {
    return {
      items: templates.items,
      getItemsAsyncStatus: templates.getItemsAsyncStatus,
      params: {
        search: templates.params.search,
        locale: templates.params.locale_translations,
      },
      hasNextPage: templates.hasNextPage,
      getItems: () => templates.getItems().then((resp) => resp.data.templates),
      paramsChange: (params, fT) => {
        return templates
          .paramsChange(
            {
              search: params.search,
              locale_translations:
                params.locale === DEFAULT_LANG_CODE ? '' : params.locale,
            },
            fT,
          )
          .then((resp) => (resp ? resp.data.templates : templates.items));
      },
      nextPage: () => templates.nextPage().then((resp) => resp.data.templates),
    };
  }

  @boundMethod
  updateKeybindings(keybindings: InputRichTextProps['keybindings']): void {
    this.setState({ keybindings });
  }

  render() {
    const {
      id,
      className,
      label,
      required,
      channel,
      attachmentsFields,
      attachments,
      hasFFWriteHTMLEmail: isAllowedToWriteHTMLEmails,
      hasFFRecommendedQuickReplies: hasRecommendedQuickReplies,
      hasLanguageTranslationControlFF,
      editorWindow = window,
      onKeyDown,
      onMount,
      onUnmount,
      templates,
      language,
      hasBorder,
      autocomplete,
      stateBarClassName,
      isPublicReply,
      toolbarAddons,
    } = this.props;

    let sanitizationProfile = channelToSanitizationProfile[channel];

    if (sanitizationProfile === 'email' && !isAllowedToWriteHTMLEmails) {
      sanitizationProfile = 'widgetchat';
    }

    return (
      <div className={styles.editor}>
        {(required || (!required && label)) && (
          <label className="control-label" htmlFor={undefined}>
            {label} {required ? '*' : ''}
          </label>
        )}
        {this.props.validation && (
          <StateBar
            channel={channel}
            text={this.state.editorText}
            validation={this.props.validation}
            className={stateBarClassName}
            isPublicReply={isPublicReply}
          />
        )}

        <RichTextEditorKeybindings onChange={this.updateKeybindings} />

        <InputRichText
          id={id}
          name=""
          value=""
          keybindings={this.state.keybindings}
          onChange={this.handleChange}
          wrapperClassName={classnames(styles.editorWrapper, {
            [styles.editorWrapperNoBorder]: !hasBorder,
          })}
          className={classnames(className, styles.editorRoot)}
          ref={this.editorRef}
          languages={
            hasLanguageTranslationControlFF
              ? this.getLanguages(language)
              : undefined
          }
          renderQuickResponse={
            hasRecommendedQuickReplies ? undefined : this.renderTemplate
          }
          quickResponsesApi={
            hasRecommendedQuickReplies
              ? undefined
              : this.getQuickResponsesApi(templates)
          }
          toolbarAddons={toolbarAddons}
          sendOnEnter={this.sendOnEnter()}
          onTriggerSend={this.handleTriggerSend}
          onToggleSendOnEnter={this.handleToggleSendOnEnter}
          placeholder={this.getPlaceholderText()}
          onFileDropped={this.handleInlineUpload}
          fileUploadStatuses={this.state.fileStatuses}
          allowFormatting={this.isFormattingAllowed()}
          sanitize={SanitizeHTML.sanitizerForProfile(sanitizationProfile)}
          allowInlineAttachments={['email', 'contactform', 'notes'].includes(
            channel,
          )}
          allowAttachments={[
            'adachat',
            'digitalgeniuschat',
            'kindlychat',
            'email',
            'contactform',
            'widgetchat',
            'messenger',
          ].includes(channel)}
          hideSendOnEnterToggle={
            ['email', 'contactform', 'notes'].includes(channel) ||
            this.props.hideSendOnEnterToggle
          }
          attachmentsFields={attachmentsFields}
          isAttachmentsUploading={this.state.filesUploadingCount > 0}
          attachments={attachments}
          onAttachmentsFile={this.handleAttachmentUpload}
          editorWindow={editorWindow}
          onMount={onMount}
          onUnmount={onUnmount}
          onError={this.handleError}
          onKeyDown={onKeyDown}
          autocomplete={autocomplete}
        />
      </div>
    );
  }
}

const richTextEditorGroup: CommandGroup = 'richTextEditor';

type RichTextEditorKeybindingsProps = {
  onChange: (
    inputRichTextKeybindings: InputRichTextProps['keybindings'],
  ) => void;
};

const RichTextEditorKeybindings = ({
  onChange,
}: RichTextEditorKeybindingsProps): null => {
  const { keybindings } = useCommands();

  useEffect(() => {
    const inputRichTextKeybindings = Object.fromEntries(
      Object.entries(keybindings)
        .filter(([commandName]) => commandName.startsWith(richTextEditorGroup))
        .map(([commandName, keyCombinations]) => {
          return [
            commandName.replace(
              `${richTextEditorGroup}${commandGroupDelimiter}`,
              '',
            ),
            keyCombinations.map((keyCombination) => keyCombination.binding),
          ];
        }),
    ) as InputRichTextProps['keybindings'];

    onChange(inputRichTextKeybindings);
  }, [keybindings, onChange]);

  return null;
};
